import { zipSync } from 'fflate';

import type { DownloadAllResult, DownloadFailure, DownloadTarget, ZipEntry } from './types';

/**
 * Fetches a file the API signed (a presigned S3 URL; the uploads bucket allows GET from the
 * app's origin) and returns its bytes.
 *
 * @param {string} url -- The signed URL.
 * @returns The bytes.
 * @throws {Error} `HTTP <status>` when the object store refuses (an expired link).
 */
export const fetchBytes = async (url: string): Promise<Uint8Array> => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return new Uint8Array(await response.arrayBuffer());
};

/**
 * Hands the browser a file to save under the given name: an object URL on an anchor with
 * `download`, which works for any origin (the `download` attribute is ignored on a cross-origin
 * href, so the signed URL itself cannot be the link).
 *
 * @param {Uint8Array} bytes -- The file.
 * @param {string} filename -- The name the browser saves it as.
 * @param {string} type -- Its media type.
 */
export const saveFile = (bytes: Uint8Array, filename: string, type: string): void => {
  const copy = new Uint8Array(bytes);
  const url = URL.createObjectURL(new Blob([copy.buffer], { type }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * A text as a file name: lower case, without accents, spaces and symbols as hyphens.
 *
 * @param {string} text -- The text (a group's or a period's name).
 * @returns The slug.
 */
export const slugOf = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * The name of the ZIP of a group's boletines: `boletines-periodo-1-quinto-a.zip`.
 *
 * @param {string} group -- The group's name.
 * @param {string} period -- The period's name.
 * @returns The file name.
 */
export const zipFilename = (group: string, period: string): string =>
  `boletines-${slugOf(period) || 'periodo'}-${slugOf(group) || 'grupo'}.zip`;

/**
 * The entries with unique names: a repeated name (two students called the same) gets " (2)",
 * " (3)"… before its extension, so no file overwrites another inside the archive.
 *
 * @param {ZipEntry[]} entries -- The files.
 * @returns The same files, renamed where needed.
 */
export const uniqueNames = (entries: ZipEntry[]): ZipEntry[] => {
  const seen = new Map<string, number>();
  return entries.map((entry) => {
    const times = (seen.get(entry.name) ?? 0) + 1;
    seen.set(entry.name, times);
    if (times === 1) return entry;
    const dot = entry.name.lastIndexOf('.');
    const stem = dot > 0 ? entry.name.slice(0, dot) : entry.name;
    const extension = dot > 0 ? entry.name.slice(dot) : '';
    return { ...entry, name: `${stem} (${times})${extension}` };
  });
};

/**
 * A ZIP with the files stored as they are (a PDF is already compressed; deflating 40 of them
 * would only cost time). fflate is 8 KB gzipped, synchronous and pure JavaScript: a 40-student
 * group is under a megabyte, which zips in a few milliseconds.
 *
 * @param {ZipEntry[]} entries -- The files.
 * @returns The archive's bytes.
 */
export const zipOf = (entries: ZipEntry[]): Uint8Array =>
  zipSync(Object.fromEntries(uniqueNames(entries).map((entry) => [entry.name, entry.bytes])), {
    level: 0,
  });

/**
 * Fetches one file per student with a few in flight at a time, keeps the group's order, and
 * turns each failure into a named row instead of stopping (the API refuses a student without
 * published grades with a 409 the screen shows next to the name).
 *
 * @param {DownloadTarget[]} targets -- The students, in the order the archive keeps.
 * @param {(target: DownloadTarget) => Promise<ZipEntry>} fetchOne -- Gets a student's file.
 * @param {object} options -- How many at a time (4 by default), a progress callback with how many
 *   have finished (fetched or failed), and how to word a failure.
 * @returns The files fetched and the students that failed.
 */
export const collectAll = async (
  targets: DownloadTarget[],
  fetchOne: (target: DownloadTarget) => Promise<ZipEntry>,
  options: {
    concurrency?: number;
    onProgress?: (done: number) => void;
    messageOf: (thrown: unknown) => string;
  },
): Promise<DownloadAllResult> => {
  const entries: (ZipEntry | null)[] = targets.map(() => null);
  const failures: DownloadFailure[] = [];
  let next = 0;
  let done = 0;
  const worker = async () => {
    while (next < targets.length) {
      const index = next;
      next += 1;
      const target = targets[index] as DownloadTarget;
      try {
        entries[index] = await fetchOne(target);
      } catch (thrown) {
        failures.push({ ...target, message: options.messageOf(thrown) });
      }
      done += 1;
      options.onProgress?.(done);
    }
  };
  const lanes = Math.max(1, Math.min(options.concurrency ?? 4, targets.length));
  await Promise.all(Array.from({ length: lanes }, () => worker()));
  const order = new Map(targets.map((target, index) => [target.studentId, index]));
  failures.sort((a, b) => (order.get(a.studentId) ?? 0) - (order.get(b.studentId) ?? 0));
  return { entries: entries.filter((entry): entry is ZipEntry => entry !== null), failures };
};
